import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { PencilSimple, X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderIndicator } from "@/modules/care-provider/utils/get-care-provider-indicator"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { DoctorType } from "@/modules/patients/information/doctors-network/doctors-network"
import { DoctorNetworkRole } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"

type DoctorInfoProps = {
  role: DoctorNetworkRole
  value: Omit<DoctorType[number]["careProvider"], "officialLanguages">
  className?: string
  dense?: boolean
  id: string
  onDelete?: (id: string) => void
  onEdit?: () => void
  readOnly?: boolean
  canHover: boolean
  careProviderId?: string
}

const DoctorInfo = ({
  value,
  className,
  dense,
  role,
  id,
  onDelete,
  onEdit,
  canHover,
  readOnly = false,
  careProviderId,
}: DoctorInfoProps) => {
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const country = useCountryParam()

  const name = combineCareProviderTitle(value)

  const handleRemove = useCallback(() => {
    confirm({
      confirmText: "common.button.remove",
      cancelText: "common.button.keep",
      title: `${t("common.button.remove")} ${name}`,
      content: "patient.information.removeDoctorContent",
      onConfirm: () => onDelete?.(id),
    })
  }, [confirm, name, t, onDelete, id])

  const firstSpecialty = value.careProviderSpecialtyTranslations?.[0]?.name

  const title = combineCareProviderTitle({
    businessName: value.businessName,
    title: value.title,
    firstName: value.firstName,
    lastName: value.lastName,
  })

  return (
    <div
      data-testid="doctor-info"
      tabIndex={0}
      id={careProviderId}
      className={cn(
        "group bg-surface focus:outline-primary-focused grid h-auto min-h-15 w-full grid-cols-[2rem_minmax(0,2fr)_minmax(0,auto)] items-center gap-4 rounded-lg p-2 transition-all ease-in-out focus:outline-hidden focus:outline-offset-0",
        dense ? "min-h-0 gap-6 py-0" : "hover:bg-surface-hovered cursor-pointer",
        className,
      )}>
      <CareProviderIcon isHoverEnabled={canHover} careProvider={value} groupType={value.careProviderGroup.groupType} />
      <div className="grid">
        <span className="text-on-surface truncate text-sm leading-5" data-testid="personal-care-provider-name">
          {title}
        </span>
        <span className="text-on-surface-subdued truncate text-xs leading-4 tracking-[0.25px]">
          <span>{joinStrings([firstSpecialty, value?.groupName], ", ")}</span>
        </span>
      </div>
      <div>
        <div className="inline-flex items-center">
          <div className="group-hover:hidden">
            {value
              ? getCareProviderIndicator({
                  isMedgatePartnerNetworkMember: value.isMedgatePartnerNetworkMember,
                  isTakesNewPatients: value.isTakesNewPatients,
                  role,
                })
              : null}
          </div>

          {onEdit && (
            <Button
              variant="circle"
              tabIndex={-1}
              data-testid="edit-care-provider-button"
              aria-label={t("admin.careProviders.dialog.dialogTitle")}
              className="h-10 w-10 items-center"
              onClick={onEdit}>
              <PencilSimple className="text-on-surface-subdued" />
            </Button>
          )}

          {!onEdit && !readOnly && role !== "GATEKEEPER" && country === "CH" && (
            <Button
              variant="circle"
              tabIndex={-1}
              data-testid="delete-care-provider-button"
              className="hidden h-10 w-10 items-center p-0 transition-all group-hover:inline-flex"
              onClick={handleRemove}>
              <X className="text-on-surface-subdued" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export { DoctorInfo }
